import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import { Button } from '@sandtech-hub-monorepo/ui-kit';

import { AppRoutesPaths } from '../../routes/types';

/**
 * The two non-failure marks, here rather than in a page, because both pages of
 * the flow use both of them.
 */
export const SUCCESS_ICON = (
  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
    <CheckCircle2 className="h-7 w-7" />
  </div>
);

export const WAITING_ICON = (
  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
    <Loader2 className="h-7 w-7 animate-spin" />
  </div>
);

interface Props {
  title: string;
  children: ReactNode;
  /** Defaults to the neutral alert mark; pass one for a non-failure state. */
  icon?: ReactNode;
  /**
   * A link element. Omitted means "back to the plans page"; `null` means the
   * state has no way out worth offering, which is true while it is loading.
   */
  action?: ReactNode | null;
}

/**
 * A whole-page state of the subscribe flow: one mark, one sentence, one way
 * out. Shared by checkout and the completion page because each of their states
 * is the entire screen — the flow either shows Stripe's iframe or it explains
 * where the learner stands — so a second layout would only be this one drawn
 * slightly differently.
 */
export const SubscribeMessage = ({ title, children, icon, action }: Props) => (
  <div className="bg-background">
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-24 text-center">
      {icon ?? (
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <AlertCircle className="h-7 w-7" />
        </div>
      )}
      <h1 className="text-2xl font-semibold text-heading">{title}</h1>
      <p className="text-muted-foreground">{children}</p>
      {action !== null && (
        <Button asChild className="mt-2 rounded-full px-8">
          {action ?? <Link to={AppRoutesPaths.Subscribe}>Back to plans</Link>}
        </Button>
      )}
    </div>
  </div>
);
